import {getFilePath} from '../../../helpers/file-helpers'
import {
  PublicZadCataloguePagination,
  PublicZadCatalogueProduct,
  PublicZadCatalogueQuery,
  PublicZadCatalogueTaxonomy,
} from '../../../models/zadPublicCatalogue'
import {sanitizePublicZadProductPromotion} from './promotionHelpers'

export function isPublicZadCataloguePath(pathname: string): boolean {
  const base = String(process.env.PUBLIC_URL || '').replace(/\/+$/, '')
  let path = pathname || '/'
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/'
  }
  if (!path.startsWith('/')) path = `/${path}`
  const normalized = path.split('?')[0].replace(/\/+$/, '') || '/'
  return normalized === '/catalogue/zad' || normalized.startsWith('/catalogue/zad/')
}

function asText(value: unknown): string {
  return value == null ? '' : String(value)
}

function asTaxonomy(value: unknown): PublicZadCatalogueTaxonomy | undefined {
  if (!value || typeof value !== 'object') return undefined
  const raw = value as Record<string, unknown>
  const name = asText(raw.name)
  const code = asText(raw.code)
  if (!name && !code) return undefined
  return {code, name}
}

export function sanitizePublicZadCatalogueProduct(value: unknown): PublicZadCatalogueProduct | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const id = asText(raw.id || raw._id)
  if (!id) return null
  const salePrice = typeof raw.salePrice === 'number' ? raw.salePrice : Number(raw.salePrice)
  return {
    id,
    productCode: asText(raw.productCode || raw.code),
    barcode: raw.barcode == null || raw.barcode === '' ? null : asText(raw.barcode),
    name: asText(raw.name),
    imageUrl: raw.imageUrl == null || raw.imageUrl === '' ? null : asText(raw.imageUrl),
    publicDescription: asText(raw.publicDescription),
    salePrice: Number.isFinite(salePrice) ? salePrice : 0,
    publisher: asText(raw.publisher) || undefined,
    brand: asText(raw.brand) || undefined,
    category: asTaxonomy(raw.category),
    productType: asTaxonomy(raw.productType),
    isFeatured: raw.isFeatured === true,
    promotion: sanitizePublicZadProductPromotion(raw.promotion),
  }
}

function asPagination(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function asPublicListSource(raw: Record<string, unknown>): unknown {
  if (Array.isArray(raw.data)) return raw.data
  return raw.catalogue || raw.items || raw.products || raw.catalogueProducts
}

export function extractPublicCatalogueList(data: unknown): {
  items: PublicZadCatalogueProduct[]
} & PublicZadCataloguePagination {
  const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  const pagination = asPagination(raw.pagination)
  const source = asPublicListSource(raw)
  const items = Array.isArray(source)
    ? source
        .map(sanitizePublicZadCatalogueProduct)
        .filter((item): item is PublicZadCatalogueProduct => Boolean(item))
    : []
  const pageNumber =
    typeof raw.pageNumber === 'number'
      ? raw.pageNumber
      : typeof pagination.pageNumber === 'number'
        ? pagination.pageNumber
        : 1
  const pageCount =
    typeof raw.pageCount === 'number'
      ? raw.pageCount
      : typeof pagination.pageCount === 'number'
        ? pagination.pageCount
        : items.length || 12
  const total =
    typeof raw.total === 'number'
      ? raw.total
      : typeof pagination.total === 'number'
        ? pagination.total
        : items.length
  const totalPages =
    typeof raw.totalPages === 'number'
      ? raw.totalPages
      : typeof pagination.pages === 'number'
        ? pagination.pages
        : typeof pagination.totalPages === 'number'
          ? pagination.totalPages
          : 1
  return {
    items,
    total,
    totalPages,
    pageNumber,
    pageCount,
  }
}

export function extractPublicCatalogueItem(data: unknown): PublicZadCatalogueProduct | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  const nested = raw.data && !Array.isArray(raw.data) ? raw.data : undefined
  return (
    sanitizePublicZadCatalogueProduct(nested) ||
    sanitizePublicZadCatalogueProduct(raw.catalogue) ||
    sanitizePublicZadCatalogueProduct(raw.item) ||
    sanitizePublicZadCatalogueProduct(raw.product) ||
    sanitizePublicZadCatalogueProduct(raw)
  )
}

export function buildPublicCatalogueQuery(params: PublicZadCatalogueQuery): string {
  const query = new URLSearchParams()
  query.set('pageNumber', String(params.pageNumber > 0 ? params.pageNumber : 1))
  query.set('pageCount', String(params.pageCount > 0 ? params.pageCount : 12))
  if (params.search && params.search.trim()) query.set('search', params.search.trim())
  if (params.category && params.category.trim()) query.set('category', params.category.trim())
  if (params.featured === true) query.set('featured', 'true')
  return query.toString()
}

export function resolvePublicCatalogueImageUrl(imageUrl?: string | null): string {
  return getFilePath(imageUrl)
}

export function collectPublicCategories(
  items: PublicZadCatalogueProduct[]
): PublicZadCatalogueTaxonomy[] {
  const map = new Map<string, PublicZadCatalogueTaxonomy>()
  items.forEach((item) => {
    const category = item.category
    if (!category?.code || !category.name) return
    if (!map.has(category.code)) map.set(category.code, {code: category.code, name: category.name})
  })
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
}

export function excerptPublicDescription(value?: string, max = 90): string {
  const text = String(value || '').trim()
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max).trim()}…` : text
}

export function mergePublicCategories(
  current: PublicZadCatalogueTaxonomy[],
  incoming: PublicZadCatalogueProduct[]
): PublicZadCatalogueTaxonomy[] {
  const map = new Map(current.map((item) => [item.code, item]))
  collectPublicCategories(incoming).forEach((item) => {
    if (!map.has(item.code)) map.set(item.code, item)
  })
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
}
